import { ALL_ACTIONS, COMPLETE, UNPLANNED_ACTIONS, UNSCHEDULED_ACTIONS } from '@/constants/action';
import { useCategories } from '@/services/categories/hooks';
import { useActionsBlocksAndEvents } from '@/services/myPlan/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { useCompleteBlock } from '@/stores/useCompleteBlock';
import { ActionEventType, ActionType, BlockListItemType } from '@/types/myPlan';
import {
  calculateTotalDuration,
  calculateTotalDurationWithRecurringInRange,
  getActionsByBlockId,
  getTotalDurationOfStarredActionsInRange,
  getActionsByCategoryId,
  getCompletedActions,
  getDailyActions,
  getIncompleteActions,
  getWeeklyActions,
} from '@/utils/action';
import {
  endOfTheWeek,
  formatDateToString,
  getRecurringInstancesInRangeCompletionSummary,
  hasRecurringOccurrenceInRange,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import { getTotalDurationOfStarredActions, validateRecurrence } from '@/utils/events';
import { humanDuration, isUncategorized } from '@/utils/index';
import { formatActions, makeBlockItemKey } from '@/utils/myplan';
import {
  appendRecurringRangeCompleteShadows,
  filterIncompleteActionsForRecurringWeekRange,
  mergeWeeklyRecurringCompletedShadows,
} from '@/utils/recurringCompletedShadow';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { isEmpty, orderBy } from 'lodash';
import { useCallback, useMemo } from 'react';

const useCaptureListData = (selectedSegmented = ALL_ACTIONS) => {
  const { data } = useActionsBlocksAndEvents();
  const { data: categoriesData } = useCategories();

  const isDailyPlanPage = useIsDailyPlanPage();

  const { selectedDate } = useCalendarMonthlyStore();
  const { data: weeklyPlanId } = useWeeklyPlanId(selectedDate);

  //!TODO: investigate why this is required (it's set in daily/weekly plan where this is used)
  const { blockId: blockToCompleteId } = useCompleteBlock();

  const filterIncompleteActions = (actions: ActionEventType[]) => {
    return actions.filter((action) => action.action.progressStatus !== COMPLETE);
  };

  const filterCompleteActions = (actions: ActionEventType[]) => {
    return actions.filter((action) => action.action.progressStatus === COMPLETE);
  };

  const filterPlannedActions = (actions: ActionEventType[]) => {
    return actions.filter((action) => !!action.action.scheduledDate);
  };

  const filterWeeklyScheduledActions = (actions: ActionEventType[]) => {
    return actions.filter((action) => action.action.scheduledTime !== null);
  };

  const filterWeeklyUnscheduledActions = (actions: ActionEventType[]) => {
    return actions.filter((action) => action.action.scheduledTime === null);
  };

  const filterWeeklyUnplannedActions = useCallback((actions: ActionEventType[]) => {
    return actions.filter((action) => {
      const noDay = action.action.scheduledDate === null;
      return noDay;
    });
  }, []);

  const filterDailyScheduledActions = useCallback(
    (actions: ActionEventType[]) => {
      return actions.filter((action) => {
        if (!action.action.scheduledDate) {
          return false;
        }

        const eventDate = utcToLocalDate(
          action.action.scheduledDate,
          action.action.scheduledTime,
          action.action.timezone,
          action.action.gmtOffset,
        );

        const fullDate = action?.action?.event?.start ? action.action.event.start.split('T')[0] : null;

        const fullEndDate = action?.action?.event?.recurrence?.rules?.[0].until
          ? action?.action?.event?.recurrence?.rules?.[0].until.split('T')[0]
          : null;

        const recurrenceEventDate = fullDate
          ? utcToLocalDate(fullDate, action.action.scheduledTime, action.action.timezone, action.action.gmtOffset)
          : null;

        const recurrenceEventEndDate = fullEndDate
          ? utcToLocalDate(fullEndDate, action.action.scheduledTime, action.action.timezone, action.action.gmtOffset)
          : null;

        const recurrence = action?.action?.event?.recurrence;

        const exceptions =
          recurrence?.exceptions?.add?.map((exception: { date: string | Date }) => {
            const parsedDate = typeof exception.date === 'string' ? parseISO(exception.date) : exception.date;
            return formatDateToString(parsedDate);
          }) || [];

        const isDailyScheduled =
          formatDateToString(eventDate) === formatDateToString(selectedDate) &&
          !exceptions.includes(formatDateToString(selectedDate));

        const hasRecurrenceRules =
          validateRecurrence(recurrence) &&
          recurrenceEventDate &&
          formatDateToString(recurrenceEventDate) <= formatDateToString(selectedDate) &&
          !exceptions.includes(formatDateToString(selectedDate)) &&
          (!recurrenceEventEndDate ||
            formatDateToString(recurrenceEventEndDate) >= formatDateToString(selectedDate));

        return isDailyScheduled || hasRecurrenceRules;
      });
    },
    [selectedDate],
  );

  const filterDailyUnscheduledActions = useCallback(
    (actions: ActionEventType[]) => {
      return actions.filter((action) => {
        const sameDayWithoutTime =
          action.action.scheduledDate === format(selectedDate, 'yyyy-MM-dd') && action.action.scheduledTime === null;
        return sameDayWithoutTime;
      });
    },
    [selectedDate],
  );

  const formattedBlocks = useMemo<BlockListItemType[]>(() => {
    if (!data) {
      return [];
    }

    const field = isDailyPlanPage ? 'dailyOrder' : 'weeklyOrder';

    const block = orderBy(data.block, field, 'asc');

    return block
      .map((block) => {
        const key = makeBlockItemKey(block.id);

        let blockActions = getActionsByBlockId(block.id, data.action) as ActionType[];

        if (isEmpty(blockActions)) {
          return {
            ...block,
            key,
            totalDuration: '0',
            starredActionsTotalDuration: '0',
          };
        }

        if (isDailyPlanPage) {
          blockActions = getDailyActions(blockActions, selectedDate) as ActionType[];
        } else {
          blockActions = getWeeklyActions(blockActions, weeklyPlanId ?? '') as ActionType[];
        }

        blockActions = blockActions.map((action) => ({
          ...action,
          isLastBlockAction: blockActions.length === 1,
          typeName: 'Action',
        }));

        let allActions = formatActions(blockActions);

        if (selectedSegmented === UNSCHEDULED_ACTIONS) {
          allActions = isDailyPlanPage
            ? filterDailyUnscheduledActions(allActions)
            : filterWeeklyUnscheduledActions(allActions);
        }

        if (selectedSegmented === UNPLANNED_ACTIONS) {
          // can only filter unplanned actions on the weekly page
          allActions = filterWeeklyUnplannedActions(allActions);
        }

        const incompleteActions = filterIncompleteActions(allActions);
        const actions = incompleteActions.map((action) => action.action);
        const weekStart = startOfTheWeek(selectedDate);
        const weekEnd = endOfTheWeek(selectedDate);
        const totalDuration = humanDuration(
          (isDailyPlanPage
            ? calculateTotalDuration(actions)
            : calculateTotalDurationWithRecurringInRange(actions, weekStart, weekEnd)) * 60,
        );
        const starredActionsTotalDuration = isDailyPlanPage
          ? getTotalDurationOfStarredActions(actions)
          : getTotalDurationOfStarredActionsInRange(actions, weekStart, weekEnd);

        return {
          ...block,
          key,
          actions: allActions.map((action) => action.action),
          totalDuration,
          starredActionsTotalDuration,
        };
      })
      .filter((block) => (block.id === blockToCompleteId ? true : block.actions?.length));
  }, [
    data,
    selectedSegmented,
    selectedDate,
    weeklyPlanId,
    blockToCompleteId,
    isDailyPlanPage,
    filterWeeklyUnplannedActions,
    filterDailyUnscheduledActions,
  ]);

  const plannedActions = useMemo(() => {
    if (!data?.action) {
      return [];
    }
    return filterPlannedActions(formatActions(data.action));
  }, [data?.action]);

  const scheduledActions = useMemo(() => {
    if (!data?.action) {
      return [];
    }
    return isDailyPlanPage
      ? filterDailyScheduledActions(formatActions(data.action))
      : filterWeeklyScheduledActions(formatActions(data.action));
  }, [data?.action, filterDailyScheduledActions, isDailyPlanPage]);

  const filterActionsBySegment = useMemo(() => {
    if (!data) {
      return [];
    }

    let nonBlockActions = data.action
      .filter((action) => action.blockId === null)
      .map((action) => ({
        ...action,
        typeName: 'Action',
      }));

    if (selectedSegmented === UNSCHEDULED_ACTIONS) {
      return isDailyPlanPage
        ? filterDailyUnscheduledActions(formatActions(nonBlockActions))
        : filterWeeklyUnscheduledActions(formatActions(nonBlockActions));
    }

    if (selectedSegmented === UNPLANNED_ACTIONS) {
      // can only filter unplanned actions on the weekly page
      return filterWeeklyUnplannedActions(formatActions(nonBlockActions));
    }

    return isDailyPlanPage
      ? filterDailyScheduledActions(formatActions(nonBlockActions))
      : formatActions(nonBlockActions);
  }, [
    data,
    filterDailyScheduledActions,
    filterDailyUnscheduledActions,
    selectedSegmented,
    isDailyPlanPage,
    filterWeeklyUnplannedActions,
    categoriesData,
  ]);

  const categories = useMemo(
    () =>
      categoriesData?.category?.map(
        ({ id, name, order, icon, color, role, ultimatePurpose, ultimateVision, secondaryPurposes, isArchived }) => {
          const actions = getActionsByCategoryId(filterActionsBySegment.map((action) => action.action).flat(), id);

          let incompleteActionsFiltered = getIncompleteActions(actions).filter(
            (a) => !a.project?.isArchived && !a.category.isArchived && !a.isCanceled,
          );
          let completedActionsFiltered = getCompletedActions(actions, 'modifiedAt').filter(
            (a) => !a.project?.isArchived && !a.category.isArchived && !a.isCanceled,
          );

          if (isDailyPlanPage) {
            const rangeStart = startOfDay(selectedDate);
            const rangeEnd = endOfDay(selectedDate);

            incompleteActionsFiltered = incompleteActionsFiltered.filter((a) => {
              if (!a?.event?.recurrence?.rules?.length) return true;
              if (!validateRecurrence(a.event?.recurrence)) return true;
              if (!hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd)) return false;
              const summary = getRecurringInstancesInRangeCompletionSummary(a, rangeStart, rangeEnd);
              if (summary.inRangeCount > 0 && summary.allCompleteInRange) return false;
              return true;
            });

            completedActionsFiltered = appendRecurringRangeCompleteShadows(
              completedActionsFiltered,
              actions,
              rangeStart,
              rangeEnd,
              { requireAllCompleteInRange: true },
            );
          } else {
            const rangeStart = startOfTheWeek(selectedDate);
            const rangeEnd = endOfTheWeek(selectedDate);

            incompleteActionsFiltered = filterIncompleteActionsForRecurringWeekRange(
              incompleteActionsFiltered,
              rangeStart,
              rangeEnd,
            );

            completedActionsFiltered = mergeWeeklyRecurringCompletedShadows(
              incompleteActionsFiltered,
              completedActionsFiltered,
              actions,
              rangeStart,
              rangeEnd,
            );
          }

          return {
            id,
            name,
            order,
            icon,
            color,
            role,
            blocks: formattedBlocks.filter((block) => block.categoryId === id),
            incompleteActions: incompleteActionsFiltered,
            completedActions: completedActionsFiltered,
            canceledActions: getActionsByCategoryId(
              actions.filter((a) => a.isCanceled && !a.project?.isArchived && !a.category.isArchived),
              id,
            ),
            plannedActions: getActionsByCategoryId(
              plannedActions.map((action) => action.action),
              id,
            ),
            scheduledActions: getActionsByCategoryId(
              scheduledActions.map((action) => action.action),
              id,
            ),
            ultimatePurpose,
            ultimateVision,
            secondaryPurposes,
            isArchived,
          };
        },
      ),
    [
      categoriesData,
      filterActionsBySegment,
      formattedBlocks,
      plannedActions,
      scheduledActions,
      isDailyPlanPage,
      selectedDate,
    ],
  );

  const emptyCategories = useMemo(
    () =>
      (categories || []).filter(
        (c) =>
          !isUncategorized(c) &&
          c.completedActions.length === 0 &&
          c.incompleteActions.length === 0 &&
          c.blocks.length === 0,
      ),
    [categories],
  );

  const notEmptyCategories = useMemo(() => {
    if (selectedSegmented === UNPLANNED_ACTIONS) {
      return (categories || []).filter(
        (c) =>
          isUncategorized(c) ||
          c.completedActions.length > 0 ||
          c.canceledActions.length > 0 ||
          c.incompleteActions.length > 0 ||
          c.blocks.length > 0 ||
          c.plannedActions.length > 0,
      );
    }
    if (selectedSegmented === UNSCHEDULED_ACTIONS) {
      return (categories || []).filter(
        (c) =>
          isUncategorized(c) ||
          c.completedActions.length > 0 ||
          c.canceledActions.length > 0 ||
          c.incompleteActions.length > 0 ||
          c.blocks.length > 0 ||
          c.scheduledActions.length > 0,
      );
    }
    return (categories || []).filter(
      (c) =>
        isUncategorized(c) ||
        c.completedActions.length > 0 ||
        c.incompleteActions.length > 0 ||
        c.canceledActions.length > 0 ||
        c.blocks.length > 0,
    );
  }, [categories, selectedSegmented, categoriesData]);

  return {
    formattedBlocks,
    categories,
    emptyCategories,
    notEmptyCategories,
    filterCompleteActions,
    filterIncompleteActions,
    filterDailyUnscheduledActions,
    filterWeeklyUnplannedActions,
    filterDailyScheduledActions,
  };
};

export default useCaptureListData;
